import React from 'react';
import { Box, Container, Typography, Paper, Grid, Chip, Card, CardContent } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[2],
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
}));

const ClientCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const clients = [
  {
    name: 'Vistara Airlines',
    logo: '/images/logos/vistara.png',
    duration: '2018 - 2024',
    teamSize: '38 Full-time Employees',
    technologies: ['Development', 'Security', 'Networking', 'Database'],
    description: 'Comprehensive development, security, networking, and database support with our largest team of 38 full-time employees (2018-2024).'
  },
  {
    name: 'Quest Diagnostics',
    logo: '/images/logos/quest-diagnostics.jpeg',
    duration: '2020 - Present',
    teamSize: '22 Full-time Employees',
    technologies: ['Development', 'Security', 'Networking', 'Infrastructure', 'Database', 'QA'],
    description: 'Comprehensive support across development, security, networking, infrastructure, database, and QA with our team of 22 full-time employees.'
  },
  {
    name: 'FreddieMac',
    logo: '/images/logos/freddiemac.png',
    duration: '2018 - Present',
    teamSize: '12 Full-time Employees',
    technologies: ['Network Security', 'Infrastructure', 'Security Projects'],
    description: 'Long-term network and security project support with our largest team of 12 full-time employees.'
  },
  {
    name: 'UPMC',
    logo: '/images/logos/upmc.png',
    duration: '2018 - Present',
    teamSize: '8 Full-time Employees',
    technologies: ['.NET', 'Networking', 'Security'],
    description: 'Comprehensive .NET, networking, and security implementation with our team of 8 full-time employees.'
  },
  {
    name: 'NCDOT',
    logo: '/images/logos/ncdot.png',
    duration: 'June 2020 - Present',
    teamSize: '8 Full-time Employees',
    technologies: ['Oracle HCM', 'Web Development'],
    description: 'End-to-end Oracle HCM implementation and support with our team of 8 full-time employees. We have also provided web development support to enhance their digital infrastructure.'
  },
  {
    name: 'Boeing',
    logo: '/images/logos/boeing.png',
    duration: 'Jan 2020 - Present',
    teamSize: '6 Full-time Employees',
    technologies: ['System Integration', 'Software Enhancements'],
    description: 'Ongoing system integration and existing software enhancements with our dedicated team of 6 full-time employees.'
  },
  {
    name: 'Tennessee Valley Authority',
    logo: '/images/logos/tva.png',
    duration: '2020 - Present',
    teamSize: '6 Full-time Employees',
    technologies: ['.NET', 'Security', 'Networking', 'Database'],
    description: 'Multi-faceted project implementation covering .NET, security, networking, and database technologies with our team of 6 full-time employees.'
  },
  {
    name: 'Red Wing Shoe',
    logo: '/images/logos/red-wing-shoe.png',
    duration: 'March 2022 - Present',
    teamSize: '5 Full-time Employees',
    technologies: ['.NET', 'Blazor', 'Full-stack Development'],
    description: 'Full-stack .NET and Blazor application development and support with our team of 5 full-time employees.'
  },
  {
    name: 'Collins Aerospace',
    logo: '/images/logos/collins-aerospace.png',
    duration: 'Sep 2023 - Present',
    teamSize: '4 Full-time Employees',
    technologies: ['SAP', 'Talend', 'Kinaxis', 'Software Engineering'],
    description: 'Comprehensive support across SAP, Talend, Kinaxis, and software engineering with our team of 4 full-time employees.'
  },
  {
    name: 'Netchex',
    logo: '/images/logos/netchex.png',
    duration: '2024 - Present',
    teamSize: '2 Full-time Employees',
    technologies: ['Software Development', 'SME Support'],
    description: 'Software development project support as Subject Matter Experts with our team of 2 full-time employees.'
  }
];

const About: React.FC = () => {
  const navigate = useNavigate();

  const handleClientClick = (clientName: string) => {
    const clientSlug = clientName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    navigate(`/client/${clientSlug}`);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 8,
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          component="h1"
          gutterBottom
          sx={{
            textAlign: 'center',
            mb: 6,
            color: 'primary.main',
            fontWeight: 'bold',
          }}
        >
          About Us
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <StyledPaper>
              <Typography variant="h4" component="h2" gutterBottom color="primary">
                Who We Are
              </Typography>
              <Typography variant="body1" paragraph>
                We are a team of passionate technologists, educators, and industry experts dedicated to bridging the gap between education and employment in the tech industry. Our mission is to empower individuals with the skills and knowledge they need to succeed in today's digital world.
              </Typography>
            </StyledPaper>
          </Grid>

          <Grid item xs={12} md={6}>
            <StyledPaper>
              <Typography variant="h4" component="h2" gutterBottom color="primary">
                What We Do
              </Typography>
              <Typography variant="body1" paragraph>
                We provide comprehensive tech services through our platform, offering networking opportunities and career guidance. Our services include:
              </Typography>
              <ul>
                <li>Professional networking and mentorship</li>
                <li>Career development resources</li>
                <li>Company partnerships and job placements</li>
                <li>Skill verification and assessment</li>
              </ul>
            </StyledPaper>
          </Grid>

          <Grid item xs={12} md={6}>
            <StyledPaper>
              <Typography variant="h4" component="h2" gutterBottom color="primary">
                How We Started
              </Typography>
              <Typography variant="body1" paragraph>
                Founded in 2014, our journey began with a simple observation: the tech industry's rapid growth was creating a significant skills gap. We recognized the need for a platform that could effectively connect aspiring professionals with the right resources and opportunities.
              </Typography>
            </StyledPaper>
          </Grid>

          <Grid item xs={12} md={6}>
            <StyledPaper>
              <Typography variant="h4" component="h2" gutterBottom color="primary">
                Our Future Vision
              </Typography>
              <Typography variant="body1" paragraph>
                We envision a future where:
              </Typography>
              <ul>
                <li>Every individual has access to quality tech opportunities</li>
                <li>Companies find the perfect talent match</li>
                <li>The tech industry becomes more diverse and inclusive</li>
                <li>Career growth is seamlessly connected to skill verification</li>
              </ul>
            </StyledPaper>
          </Grid>

          {/* Our Clients Section */}
          <Grid item xs={12}>
            <StyledPaper>
              <Typography variant="h4" component="h2" gutterBottom color="primary" sx={{ textAlign: 'center', mb: 4 }}>
                Our Trusted Clients
              </Typography>
              <Typography variant="body1" paragraph sx={{ textAlign: 'center', mb: 4, color: 'text.secondary' }}>
                We're proud to partner with industry leaders across various sectors, delivering exceptional IT services and solutions that drive their success.
              </Typography>
              
              <Grid container spacing={3}>
                {clients.map((client, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <ClientCard 
                      onClick={() => handleClientClick(client.name)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                        }
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box
                            component="img"
                            src={client.logo}
                            alt={`${client.name} logo`}
                            sx={{
                              height: 60,
                              width: 'auto',
                              maxWidth: 160,
                              objectFit: 'contain',
                              mr: 2,
                              backgroundColor: '#ffffff',
                              borderRadius: 1,
                              padding: 1.2,
                              border: '1px solid #e0e0e0',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            }}
                            onError={(e) => {
                              // Hide the image and show a colored box with initials
                              e.currentTarget.style.display = 'none';
                              const fallback = e.currentTarget.nextSibling as HTMLElement;
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                          <Box
                            sx={{
                              height: 50,
                              width: 50,
                              backgroundColor: 'primary.main',
                              color: 'white',
                              display: 'none',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 1,
                              mr: 2,
                              fontWeight: 'bold',
                              fontSize: '1rem',
                            }}
                          >
                            {client.name.split(' ').map(word => word[0]).join('').substring(0, 2)}
                          </Box>
                          <Typography 
                            variant="h6"
                            sx={{ 
                              fontWeight: 600, 
                              color: 'primary.main'
                            }}
                          >
                            {client.name}
                          </Typography>
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          <strong>Duration:</strong> {client.duration}
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          <strong>Team Size:</strong> {client.teamSize}
                        </Typography>
                        
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          {client.description}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {client.technologies.map((tech, techIndex) => (
                            <Chip
                              key={techIndex}
                              label={tech}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </ClientCard>
                  </Grid>
                ))}
              </Grid>
              
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      backgroundColor: 'primary.main', 
                      color: 'white', 
                      padding: 3, 
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                    }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                        800+
                      </Typography>
                      <Typography variant="h6" sx={{ opacity: 0.9 }}>
                        Total Team Members
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      backgroundColor: 'secondary.main', 
                      color: 'white', 
                      padding: 3, 
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)'
                    }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                        120+
                      </Typography>
                      <Typography variant="h6" sx={{ opacity: 0.9 }}>
                        Clients Supporting
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                <Typography variant="h6" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                  Industries We Serve
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  Supporting clients across multiple industries with comprehensive IT solutions and professional services
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5 }}>
                  {[
                    'Aerospace & Defense', 'Healthcare & Life Sciences', 'Financial Services', 
                    'Retail & E-commerce', 'Government & Public Sector', 'Manufacturing', 
                    'Technology & Software', 'Energy & Utilities', 'Education', 'Real Estate',
                    'Transportation & Logistics', 'Media & Entertainment', 'Consulting', 'Non-Profit'
                  ].map((industry, index) => (
                    <Box
                      key={index}
                      sx={{
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                        color: 'primary.main',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        border: '1px solid rgba(25, 118, 210, 0.2)'
                      }}
                    >
                      {industry}
                    </Box>
                  ))}
                </Box>
              </Box>
            </StyledPaper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default About; 